import { useMemo, useState } from 'react';
import { sb } from '../../../lib/supabase';
import {
  visibleItems,
  useInlineEdit,
  insertLookup,
  renameLookup,
  toggleLookupActive,
  StatusPill,
} from './utils';

export default function TeamsTree({ teams, subTeams, orgId, setError, reload }) {
  const [showInactive, setShowInactive] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [addingTeam, setAddingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [addingSubFor, setAddingSubFor] = useState(null);
  const [newSubName, setNewSubName] = useState('');

  const filteredTeams = visibleItems(teams, showInactive);
  const filteredSubTeams = visibleItems(subTeams, showInactive);

  const grouped = useMemo(() => filteredTeams.map((team) => ({
    ...team,
    subTeams: filteredSubTeams.filter((st) => st.team_id === team.id),
  })), [filteredTeams, filteredSubTeams]);

  const orphanSubTeams = useMemo(
    () => filteredSubTeams.filter((st) => !st.team_id || !teams.some((t) => t.id === st.team_id)),
    [filteredSubTeams, teams],
  );

  function toggleCollapsed(teamId) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }

  async function addTeam() {
    if (!newTeamName.trim()) return;
    setError(null);
    const { error } = await insertLookup(orgId, 'resource_teams', newTeamName);
    if (error) setError(error.message);
    else { setNewTeamName(''); setAddingTeam(false); reload(); }
  }

  async function addSubTeam(teamId) {
    if (!newSubName.trim()) return;
    setError(null);
    const { error } = await sb.from('resource_sub_teams').insert({
      organisation_id: orgId,
      team_id: teamId,
      name: newSubName.trim(),
    });
    if (error) setError(error.message);
    else { setNewSubName(''); setAddingSubFor(null); reload(); }
  }

  async function renameTeam(id, name) {
    setError(null);
    const { error } = await renameLookup('resource_teams', id, name);
    if (error) setError(error.message);
    else reload();
  }

  async function renameSubTeam(id, name) {
    setError(null);
    const { error } = await renameLookup('resource_sub_teams', id, name);
    if (error) setError(error.message);
    else reload();
  }

  async function toggleTeamActive(id, isCurrentlyActive) {
    setError(null);
    const { error } = await toggleLookupActive('resource_teams', id, isCurrentlyActive);
    if (error) setError(error.message);
    else reload();
  }

  async function toggleSubActive(id, isCurrentlyActive) {
    setError(null);
    const { error } = await toggleLookupActive('resource_sub_teams', id, isCurrentlyActive);
    if (error) setError(error.message);
    else reload();
  }

  return (
    <div className="rd-pane">
      <div className="rd-chead">
        <div>
          <h2>Teams &amp; sub-teams</h2>
          <p>Sub-teams nest under their team — no separate list, no picker.</p>
        </div>
        <div className="rd-chead-actions">
          <button type="button" className="btn-ghost sm" onClick={() => setShowInactive((v) => !v)}>
            {showInactive ? 'Hide inactive' : 'Show inactive'}
          </button>
          <button type="button" className="rd-add-btn" onClick={() => setAddingTeam(true)}>+ Add team</button>
        </div>
      </div>

      <div className="rd-list-panel">
        {addingTeam && (
          <div className="rd-add-row">
            <input
              type="text"
              className="form-input"
              placeholder="Team name"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTeamName.trim()) addTeam();
                if (e.key === 'Escape') { setAddingTeam(false); setNewTeamName(''); }
              }}
              autoFocus
            />
            <button type="button" className="rd-add-btn" onClick={addTeam}>Save</button>
            <button type="button" className="btn-ghost sm" onClick={() => { setAddingTeam(false); setNewTeamName(''); }}>Cancel</button>
          </div>
        )}

        {grouped.length === 0 && !addingTeam ? (
          <div className="rd-empty">No teams yet.</div>
        ) : grouped.map((team) => (
          <TeamGroup
            key={team.id}
            team={team}
            collapsed={collapsed.has(team.id)}
            onToggle={() => toggleCollapsed(team.id)}
            onRenameTeam={renameTeam}
            onRenameSub={renameSubTeam}
            onToggleTeamActive={toggleTeamActive}
            onToggleSubActive={toggleSubActive}
            addingSubFor={addingSubFor}
            setAddingSubFor={setAddingSubFor}
            newSubName={newSubName}
            setNewSubName={setNewSubName}
            onAddSubTeam={addSubTeam}
            setError={setError}
          />
        ))}

        {orphanSubTeams.length > 0 && (
          <div className="rd-orphan-block">
            <div className="rd-orphan-label">Unassigned sub-teams</div>
            {orphanSubTeams.map((st) => (
              <SubTeamRow
                key={st.id}
                item={st}
                onRename={renameSubTeam}
                onToggleActive={toggleSubActive}
                setError={setError}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamGroup({
  team,
  collapsed,
  onToggle,
  onRenameTeam,
  onRenameSub,
  onToggleTeamActive,
  onToggleSubActive,
  addingSubFor,
  setAddingSubFor,
  newSubName,
  setNewSubName,
  onAddSubTeam,
  setError,
}) {
  const teamEdit = useInlineEdit(onRenameTeam, setError);
  const inactive = team.is_active === false;

  return (
    <div className={`rd-team-group${collapsed ? ' collapsed' : ''}`}>
      <div className="rd-team-head">
        <button type="button" className="rd-team-toggle" onClick={onToggle}>
          <span className="rd-chev">▾</span>
          {teamEdit.editingId === team.id ? (
            <input
              className="form-input rd-edit-input"
              value={teamEdit.editName}
              onChange={(e) => teamEdit.setEditName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') teamEdit.saveEdit(team.id);
                if (e.key === 'Escape') teamEdit.cancelEdit();
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <span className="rd-team-name">{team.name}</span>
          )}
        </button>
        <span className="rd-acts">
          {teamEdit.editingId === team.id ? (
            <>
              <button type="button" className="rd-link" onClick={() => teamEdit.saveEdit(team.id)}>Save</button>
              <button type="button" className="rd-link muted" onClick={teamEdit.cancelEdit}>Cancel</button>
            </>
          ) : (
            <>
              <button type="button" className="rd-link" onClick={() => teamEdit.startEdit(team)}>Edit</button>
              <StatusPill active={!inactive} />
              <button type="button" className="rd-link" onClick={() => onToggleTeamActive(team.id, !inactive)}>
                {inactive ? 'Reactivate' : 'Deactivate'}
              </button>
            </>
          )}
        </span>
      </div>

      {!collapsed && (
        <>
          <div className="rd-sub-list">
            {team.subTeams.length === 0 ? (
              <div className="rd-sub-empty">No sub-teams yet.</div>
            ) : team.subTeams.map((st) => (
              <SubTeamRow
                key={st.id}
                item={st}
                onRename={onRenameSub}
                onToggleActive={onToggleSubActive}
                setError={setError}
              />
            ))}
          </div>
          <div className="rd-add-sub">
            {addingSubFor === team.id ? (
              <div className="rd-add-row inline">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Sub-team name"
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSubName.trim()) onAddSubTeam(team.id);
                    if (e.key === 'Escape') { setAddingSubFor(null); setNewSubName(''); }
                  }}
                  autoFocus
                />
                <button type="button" className="rd-add-btn sm" onClick={() => onAddSubTeam(team.id)}>Save</button>
                <button type="button" className="btn-ghost sm" onClick={() => { setAddingSubFor(null); setNewSubName(''); }}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="rd-link" onClick={() => { setAddingSubFor(team.id); setNewSubName(''); }}>
                + Add sub-team
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SubTeamRow({ item, onRename, onToggleActive, setError }) {
  const edit = useInlineEdit(onRename, setError);
  const inactive = item.is_active === false;

  return (
    <div className={`rd-sub-row${inactive ? ' muted' : ''}`}>
      {edit.editingId === item.id ? (
        <input
          className="form-input rd-edit-input"
          value={edit.editName}
          onChange={(e) => edit.setEditName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') edit.saveEdit(item.id);
            if (e.key === 'Escape') edit.cancelEdit();
          }}
          autoFocus
        />
      ) : (
        <span>{item.name}</span>
      )}
      <span className="rd-acts">
        {edit.editingId === item.id ? (
          <>
            <button type="button" className="rd-link" onClick={() => edit.saveEdit(item.id)}>Save</button>
            <button type="button" className="rd-link muted" onClick={edit.cancelEdit}>Cancel</button>
          </>
        ) : (
          <>
            <button type="button" className="rd-link" onClick={() => edit.startEdit(item)}>Edit</button>
            <button type="button" className="rd-link" onClick={() => onToggleActive(item.id, !inactive)}>
              {inactive ? 'Reactivate' : 'Deactivate'}
            </button>
          </>
        )}
      </span>
    </div>
  );
}
